import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, Check, X, Loader2, Music, Pencil, ChevronUp, ChevronDown } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useWorkspace } from "@/hooks/use-workspace";

interface PlatformTalentType {
  id: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
}

interface WorkspaceTalentType {
  id: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
}

interface WorkspaceTalentTypeCatalog {
  platformTypes: PlatformTalentType[];
  workspaceTypes: WorkspaceTalentType[];
  hiddenPlatformTypeIds: string[];
}

interface ManageTalentTypesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inPanel?: boolean;
}

export function ManageTalentTypesDialog({ open, onOpenChange, inPanel = false }: ManageTalentTypesDialogProps) {
  const { toast } = useToast();
  const { buildPath } = useWorkspace();
  const [newName, setNewName] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");

  const { data: catalog, isLoading } = useQuery<WorkspaceTalentTypeCatalog>({
    queryKey: ["/api/talent-types"],
    enabled: open || inPanel,
  });

  const platformTypes = catalog?.platformTypes ?? [];
  const workspaceTypes = catalog?.workspaceTypes ?? [];
  const hiddenIds = new Set(catalog?.hiddenPlatformTypeIds ?? []);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/talent-types"] });
  };

  const addCustomMutation = useMutation({
    mutationFn: async (name: string) => {
      const r = await apiRequest("POST", buildPath("/api/talent-types"), { name });
      return r.json();
    },
    onSuccess: () => {
      invalidate();
      setNewName("");
      setIsAdding(false);
      toast({ title: "Custom type added" });
    },
    onError: (err: any) => {
      toast({ title: err?.message || "Failed to add type", variant: "destructive" });
    },
  });

  const deleteCustomMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", buildPath(`/api/talent-types/custom/${id}`));
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "Custom type removed" });
    },
    onError: (err: any) => {
      toast({ title: err?.message || "Failed to remove type", variant: "destructive" });
    },
  });

  const renameCustomMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const r = await apiRequest("PATCH", buildPath(`/api/talent-types/custom/${id}`), { name });
      return r.json();
    },
    onSuccess: () => {
      invalidate();
      setEditingId(null);
      setEditName("");
      toast({ title: "Custom type renamed" });
    },
    onError: (err: any) => {
      toast({ title: err?.message || "Failed to rename type", variant: "destructive" });
    },
  });

  const reorderCustomMutation = useMutation({
    mutationFn: async (swaps: { id: string; sortOrder: number }[]) => {
      await Promise.all(
        swaps.map((s) => apiRequest("PATCH", buildPath(`/api/talent-types/custom/${s.id}`), { sortOrder: s.sortOrder })),
      );
    },
    onSuccess: () => invalidate(),
    onError: (err: any) => {
      toast({ title: err?.message || "Failed to reorder types", variant: "destructive" });
    },
  });

  const moveCustom = (index: number, direction: -1 | 1) => {
    const a = workspaceTypes[index];
    const b = workspaceTypes[index + direction];
    if (!a || !b) return;
    reorderCustomMutation.mutate([
      { id: a.id, sortOrder: b.sortOrder },
      { id: b.id, sortOrder: a.sortOrder },
    ]);
  };

  const toggleHideMutation = useMutation({
    mutationFn: async ({ platformTypeId, hide }: { platformTypeId: string; hide: boolean }) => {
      if (hide) {
        await apiRequest("POST", buildPath("/api/talent-types/hide"), { platformTalentTypeId: platformTypeId });
      } else {
        await apiRequest("DELETE", buildPath(`/api/talent-types/hide/${platformTypeId}`));
      }
    },
    onSuccess: () => invalidate(),
    onError: (err: any) => {
      toast({ title: err?.message || "Failed to update visibility", variant: "destructive" });
    },
  });

  const innerContent = (
    <div className="space-y-6">
      {/* Platform defaults — toggle visibility */}
      <div className="space-y-3">
        <div>
          <h4 className="text-sm font-medium">Platform Defaults</h4>
          <p className="text-xs text-muted-foreground mt-0.5">
            Toggle off types you don't use. Talent already tagged with a hidden type keeps its value.
          </p>
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="border border-border rounded-lg divide-y divide-border overflow-hidden">
            {platformTypes.map((pt) => {
              const isHidden = hiddenIds.has(pt.id);
              return (
                <div
                  key={pt.id}
                  className="flex items-center justify-between px-3 py-2.5 bg-card"
                  data-testid={`row-platform-talent-type-${pt.id}`}
                >
                  <span className={`text-sm ${isHidden ? "text-muted-foreground line-through" : ""}`}>
                    {pt.name}
                  </span>
                  <Switch
                    checked={!isHidden}
                    onCheckedChange={(checked) =>
                      toggleHideMutation.mutate({ platformTypeId: pt.id, hide: !checked })
                    }
                    disabled={toggleHideMutation.isPending}
                    data-testid={`switch-platform-talent-type-${pt.id}`}
                  />
                </div>
              );
            })}
            {platformTypes.length === 0 && (
              <div className="py-4 text-center text-sm text-muted-foreground">
                No platform defaults configured yet.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Workspace custom types */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium">Custom Types</h4>
            <p className="text-xs text-muted-foreground mt-0.5">
              Types visible only in this workspace. They never affect other workspaces.
            </p>
          </div>
          {!isAdding && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsAdding(true)}
              data-testid="button-add-custom-talent-type"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Add Custom
            </Button>
          )}
        </div>

        {isAdding && (
          <div className="flex items-center gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newName.trim()) addCustomMutation.mutate(newName.trim());
                if (e.key === "Escape") { setIsAdding(false); setNewName(""); }
              }}
              placeholder="e.g. Hypnotist"
              autoFocus
              className="h-8 text-sm flex-1"
              data-testid="input-new-custom-talent-type"
            />
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              onClick={() => newName.trim() && addCustomMutation.mutate(newName.trim())}
              disabled={!newName.trim() || addCustomMutation.isPending}
              data-testid="button-confirm-add-custom-talent-type"
            >
              {addCustomMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Check className="w-3.5 h-3.5 text-green-600" />
              )}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              onClick={() => { setIsAdding(false); setNewName(""); }}
              data-testid="button-cancel-add-custom-talent-type"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}

        {workspaceTypes.length > 0 ? (
          <div className="border border-border rounded-lg divide-y divide-border overflow-hidden">
            {workspaceTypes.map((wt, index) => {
              const isEditing = editingId === wt.id;
              return (
                <div
                  key={wt.id}
                  className="flex items-center justify-between gap-2 px-3 py-2.5 bg-card group"
                  data-testid={`row-custom-talent-type-${wt.id}`}
                >
                  {isEditing ? (
                    <>
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && editName.trim()) {
                            renameCustomMutation.mutate({ id: wt.id, name: editName.trim() });
                          }
                          if (e.key === "Escape") { setEditingId(null); setEditName(""); }
                        }}
                        autoFocus
                        className="h-8 text-sm flex-1"
                        data-testid={`input-edit-custom-talent-type-${wt.id}`}
                      />
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => editName.trim() && renameCustomMutation.mutate({ id: wt.id, name: editName.trim() })}
                        disabled={!editName.trim() || renameCustomMutation.isPending}
                        data-testid={`button-confirm-edit-custom-talent-type-${wt.id}`}
                      >
                        {renameCustomMutation.isPending ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Check className="w-3.5 h-3.5 text-green-600" />
                        )}
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => { setEditingId(null); setEditName(""); }}
                        data-testid={`button-cancel-edit-custom-talent-type-${wt.id}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm truncate">{wt.name}</span>
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">Custom</Badge>
                      </div>
                      <div className="flex items-center gap-0.5">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => moveCustom(index, -1)}
                          disabled={index === 0 || reorderCustomMutation.isPending}
                          data-testid={`button-move-up-custom-talent-type-${wt.id}`}
                        >
                          <ChevronUp className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => moveCustom(index, 1)}
                          disabled={index === workspaceTypes.length - 1 || reorderCustomMutation.isPending}
                          data-testid={`button-move-down-custom-talent-type-${wt.id}`}
                        >
                          <ChevronDown className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => { setEditingId(wt.id); setEditName(wt.name); }}
                          data-testid={`button-edit-custom-talent-type-${wt.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                          onClick={() => deleteCustomMutation.mutate(wt.id)}
                          disabled={deleteCustomMutation.isPending}
                          data-testid={`button-delete-custom-talent-type-${wt.id}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          !isAdding && (
            <p className="text-sm text-muted-foreground py-2">No custom types added yet.</p>
          )
        )}
      </div>
    </div>
  );

  if (inPanel) {
    return (
      <div className="space-y-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Music className="w-4 h-4" />
            <h3 className="font-semibold text-sm">Talent Types</h3>
          </div>
          <p className="text-xs text-muted-foreground">
            Configure which act types appear in the Talent Type dropdown when creating or editing talent.
          </p>
        </div>
        {innerContent}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Music className="w-5 h-5" />
            Manage Talent Types
          </DialogTitle>
          <DialogDescription>
            Configure which act types appear in the Talent Type dropdown when creating or editing talent.
          </DialogDescription>
        </DialogHeader>
        {innerContent}
      </DialogContent>
    </Dialog>
  );
}
